import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Spinner, Tab, Tabs } from 'react-bootstrap';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import {
  IKitter,
  IOrderItem,
  OrderSourceLabel,
} from '../../types/IOrderResponse';
import OrderSkeleton from '../order/OrderSkeleton';
import Field from '../order/Field';
import LotsCard from '../order/LotsCard';
import PopupHeader from './PopupHeader';
import SaveButton from '../SaveButton';
import useLotsDraft from '../../hooks/useLotsDraft';
import useOrderDetails from '../../hooks/reactQuery/useOrderDetails';
import DownloadIcon from '../icons/DownloadIcon';
import useGetAllKitters from '../../hooks/reactQuery/useGetAllKitters';
import PdfDocument from '../pdf/RenderFlashUrlPdf';
import CancelIcon from '../icons/CancelIcon';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import useDeleteOrder from '../../hooks/reactQuery/useDeleteOrder';
import useGetOrderDetailsByNextStatus from '../../hooks/reactQuery/useGetOrderDetailsByNextStatus';
import useOrderUpdate from '../../hooks/reactQuery/useUpdateOrder';
import formatPhone, { phoneHref } from '../../utils/formatPhone';
import getInitials from '../../utils/getInitials';
import StatusStepper from '../StatusStepper';
import { formatOrderDateTime } from '../../utils/formatOrderDate';
import { track } from '../../telemetry';

interface OrderDetailsModalProps {
  orderData: (IOrderItem & { kitter?: IKitter }) | any;
  isLoading: boolean;
  onClose?: () => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  orderData,
  isLoading,
  onClose,
}) => {
  const { data, isFetching } = useOrderDetails(orderData?.id);
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<{ kitter: IKitter } & IOrderItem & any>({
    defaultValues: data || {},
  });
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [tab, setTab] = useState<string>('order');
  const [isConfirmingShip, setIsConfirmingShip] = useState(false);
  const lotsCardRef = useRef<HTMLElement>(null);
  const lots = useLotsDraft(data);
  const { data: allKitters } = useGetAllKitters();
  const { mutate: updateOrder, isLoading: isUpdateOrderLoading } =
    useOrderUpdate(orderData?.id);
  const { mutate: deleteOrder, isLoading: isDeleteLoading } = useDeleteOrder(
    data?.id,
  );
  const { mutate: updateNextStatus, isLoading: isNextStatusLoading } =
    useGetOrderDetailsByNextStatus();

  // The Admin form follows the saved order (first load and after every save).
  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  // The confirm step lives in the footer; make sure the lots it refers to are on screen.
  useEffect(() => {
    if (isConfirmingShip) {
      lotsCardRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [isConfirmingShip]);

  // One flag for every write, so two requests can never race each other.
  const busy = isUpdateOrderLoading || isNextStatusLoading || isDeleteLoading;
  const holdForLots =
    lots.unsavedCount > 0 ? 'Save or undo the lot changes first' : undefined;
  // For telemetry: the PID from the single-order read, or the list row until it lands.
  const pid = String(data?.pid ?? orderData?.pid ?? '');

  const handleDelete = (orderId: number) => {
    deleteOrder(
      { orderId },
      {
        onSuccess: () => {
          track('order_cancelled', { pid, status: data?.status });
          toast.success(`Order Cancelled`);
          setIsCancelOpen(false);
        },
        onError: () => {
          toast.error(`Failed to delete`);
        },
      },
    );
  };

  // Admin save sends the live lots too, so an unsaved lot can never be overwritten by the
  // stale copy the form was reset with.
  const handleFormSubmit = (formData: IOrderItem) => {
    updateOrder(
      { ...formData, kitLots: lots.kitLots, boxLots: lots.boxLots },
      {
        onSuccess: () => {
          toast.success(`Order Updated`);
          reset(formData);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update`);
        },
      },
    );
  };

  const handleStatusChange = () => {
    // Advancing a "Kitting in Progress" order moves it into "Ready for Shipping".
    // Ask to confirm the lots first and save them with the status in a single PUT.
    if (data?.status === 'Kitting in Progress') {
      setTab('order');
      setIsConfirmingShip(true);
      return;
    }
    updateNextStatus(
      { id: orderData?.id },
      {
        onSuccess: () => {
          track('order_status_moved', {
            pid,
            from: data?.status,
            to: data?.nextStatus,
            withLots: false,
          });
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update status`);
        },
      },
    );
  };

  const handleSaveLots = () => {
    updateOrder(
      {
        id: data?.id ?? orderData?.id,
        status: data?.status,
        kitLots: lots.kitLots,
        boxLots: lots.boxLots,
      },
      {
        onSuccess: () => {
          track('lots_saved', {
            pid,
            kitLots: lots.kitLots.length,
            boxLots: lots.boxLots.length,
            unsavedCount: lots.unsavedCount,
          });
          toast.success(`Lots Updated`);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update lots`);
        },
      },
    );
  };

  const handleConfirmReadyForShipping = () => {
    updateOrder(
      {
        id: data?.id ?? orderData?.id,
        status: 'Ready for Shipping',
        kitLots: lots.kitLots,
        boxLots: lots.boxLots,
      },
      {
        onSuccess: () => {
          track('order_status_moved', {
            pid,
            from: data?.status,
            to: 'Ready for Shipping',
            withLots: true,
          });
          toast.success(`Order moved to Ready for Shipping`);
          setIsConfirmingShip(false);
        },
        onError: (error) => {
          toast.error(error?.message || `Failed to update`);
        },
      },
    );
  };

  const isAdmin = data?.permissions?.Self?.includes('admin-section');

  // Header and stepper draw from the list row straight away; the body shows a skeleton
  // until the single-order read lands.
  const showSkeleton = isLoading || (isFetching && !data);

  const sourceLabel =
    data?.source !== undefined ? OrderSourceLabel[data.source] ?? '' : '';
  const address = data?.shippingAddress;
  const itemRows = (
    [
      ['Kits', data?.numberOfKits ?? 0],
      ['Boxes', data?.numberOfBoxes ?? 0],
      ['Wall unit bundles', data?.numberOfWallUnitBundles ?? 0],
      [
        'Wall unit + wayfinder bundles',
        data?.numberOfWallUnitWayFinderBundles ?? 0,
      ],
      ['Wayfinders', data?.numberOfWayFinders ?? 0],
    ] as [string, number][]
  ).filter(([, count]) => count > 0);
  const phoneLink = phoneHref(data?.phoneNumber);

  // PDFDownloadLink re-renders the document whenever it gets a new element, so only build
  // one when its inputs change.
  const customerName = orderData?.customerName;
  const kitterName = data?.kitter?.name;
  const numberOfKits = data?.numberOfKits;
  const pdfDocument = useMemo(
    () => (
      <PdfDocument
        customerName={customerName}
        kitter={kitterName}
        numberOfKits={numberOfKits}
      />
    ),
    [customerName, kitterName, numberOfKits],
  );

  return (
    <>
      <div className="op">
        <PopupHeader
          title={data?.pid ?? orderData?.pid}
          status={data?.status ?? orderData?.status}
          meta={[
            orderData?.customerName,
            orderData?.orderDate
              ? `Ordered ${formatOrderDateTime(orderData.orderDate)}`
              : null,
            sourceLabel ? `via ${sourceLabel}` : null,
          ]}
          actions={
            data ? (
              <PDFDownloadLink
                className="btn-app btn-app--outline"
                document={pdfDocument}
                fileName={`FTC_${data?.pid}.pdf`}
                data-track="order_pdf"
                onClick={() => track('order_pdf_downloaded', { pid })}
              >
                {({ loading }) =>
                  loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <DownloadIcon /> Flash Title PDF
                    </>
                  )
                }
              </PDFDownloadLink>
            ) : null
          }
          onClose={onClose}
        />

        <div className="op-body">
          <div className="op-stepper">
            <StatusStepper status={data?.status ?? orderData?.status} />
          </div>

          {showSkeleton ? (
            <OrderSkeleton />
          ) : (
            <div className="horizontal-tabs op-tabs">
              <Tabs
                id="order-popup-tabs"
                activeKey={tab}
                onSelect={(k) => setTab(k || 'order')}
              >
                <Tab eventKey="order" title="Order details">
                  <div className="op-grid">
                    <div className="op-col">
                      <section className="op-card">
                        <div className="op-card-head">
                          <h5>Customer</h5>
                        </div>
                        <div className="op-card-body">
                          <div className="op-who">
                            <span className="avatar-md" aria-hidden="true">
                              {getInitials(
                                orderData?.customerName,
                                data?.emailAddress,
                              )}
                            </span>
                            <div>
                              <div className="op-who-name">
                                {orderData?.customerName || '—'}
                              </div>
                              {sourceLabel ? (
                                <div className="op-who-sub">
                                  via {sourceLabel}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="op-fields">
                            <Field label="Email">
                              {data?.emailAddress ? (
                                <a href={`mailto:${data.emailAddress}`}>
                                  {data.emailAddress}
                                </a>
                              ) : (
                                '—'
                              )}
                            </Field>
                            <Field label="Phone">
                              {phoneLink ? (
                                <a href={phoneLink}>
                                  {formatPhone(data?.phoneNumber) ||
                                    data?.phoneNumber}
                                </a>
                              ) : (
                                formatPhone(data?.phoneNumber) || '—'
                              )}
                            </Field>
                          </div>
                        </div>
                      </section>

                      <section className="op-card">
                        <div className="op-card-head">
                          <h5>Items</h5>
                          {data?.kitter?.name ? (
                            <small>Kitter: {data.kitter.name}</small>
                          ) : null}
                        </div>
                        <div className="op-card-body">
                          {itemRows.length ? (
                            <div className="op-fields op-fields--tiles">
                              {itemRows.map(([label, count]) => (
                                <Field key={label} label={label} big>
                                  {count}
                                </Field>
                              ))}
                            </div>
                          ) : (
                            <div className="op-empty">
                              No items on this order.
                            </div>
                          )}
                        </div>
                      </section>

                      <section className="op-card">
                        <div className="op-card-head">
                          <h5>Shipping address</h5>
                        </div>
                        <div className="op-card-body">
                          {address?.streetAddress || address?.name ? (
                            <div className="op-fields op-fields--pairs">
                              <Field label="Name">{address?.name || '—'}</Field>
                              <Field label="Business">
                                {address?.businessName || '—'}
                              </Field>
                              <Field label="Street address" wide>
                                {[address?.streetAddress, address?.unitSuite]
                                  .filter(Boolean)
                                  .join(', ') || '—'}
                              </Field>
                              <Field label="City">{address?.city || '—'}</Field>
                              <Field label="Province">
                                {address?.province || '—'}
                              </Field>
                              <Field label="Postal code">
                                {address?.postalCode || '—'}
                              </Field>
                              <Field label="Country">Canada</Field>
                            </div>
                          ) : (
                            <div className="op-empty">No shipping address.</div>
                          )}
                        </div>
                      </section>
                    </div>

                    <div className="op-col">
                      <LotsCard
                        cardRef={lotsCardRef}
                        draft={lots}
                        kitQuantity={data?.numberOfKits}
                        boxQuantity={data?.numberOfBoxes}
                        saving={isUpdateOrderLoading}
                        disabled={busy}
                        onSave={handleSaveLots}
                        highlight={isConfirmingShip}
                        hint={
                          isConfirmingShip
                            ? 'Check the lots, then confirm below'
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </Tab>
                <Tab eventKey="admin" title="Admin">
                  <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <section className="op-card">
                      <div className="op-card-head">
                        <h5>Admin</h5>
                        <div className="op-card-actions">
                          {isAdmin ? (
                            <SaveButton
                              type="submit"
                              dirty={isDirty}
                              saving={isUpdateOrderLoading}
                              disabled={busy}
                              label="Save changes"
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="op-card-body">
                        {isAdmin ? (
                          <div className="op-admin-grid">
                            <div className="input-group">
                              <label htmlFor="status">Status</label>
                              <select
                                id="status"
                                {...register('status')}
                                className="select-field"
                              >
                                {data?.permissions?.StatusList?.map(
                                  (status: string, index: number) => (
                                    <option
                                      key={`key-${status}-${index}`}
                                      value={status}
                                    >
                                      {status}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>
                            <div className="input-group">
                              <label htmlFor="kitting">Kitter</label>
                              <select
                                id="kitting"
                                {...register('kitter.id')}
                                className="select-field"
                              >
                                {allKitters?.map(
                                  (kitter: IKitter, index: number) => (
                                    <option
                                      key={`key-${kitter.id}-${index}`}
                                      value={kitter.id}
                                    >
                                      {kitter.name}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>
                          </div>
                        ) : (
                          <div className="op-empty">
                            Status and kitter changes need an admin account.
                          </div>
                        )}
                        <div className="op-danger">
                          <div>
                            <div className="op-danger-title">
                              Cancel this order
                            </div>
                            <div className="op-danger-sub">
                              Marks the order as cancelled. This can&apos;t be
                              undone from here.
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn-app btn-app--outline btn-app--sm op-danger-btn"
                            onClick={() => setIsCancelOpen(true)}
                            disabled={busy}
                            data-track="order_cancel"
                          >
                            <CancelIcon /> Cancel order
                          </button>
                        </div>
                      </div>
                    </section>
                  </form>
                </Tab>
              </Tabs>
            </div>
          )}
        </div>

        {data?.nextStatus && !showSkeleton ? (
          <div className="op-foot">
            {isConfirmingShip ? (
              <div className="op-foot-actions">
                <button
                  type="button"
                  className="btn-app btn-app--primary op-next"
                  onClick={handleConfirmReadyForShipping}
                  disabled={busy}
                  data-track="order_confirm_ship"
                >
                  {isUpdateOrderLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    'Confirm lots & move to Ready for Shipping'
                  )}
                </button>
                <button
                  type="button"
                  className="btn-app btn-app--outline op-foot-cancel"
                  onClick={() => setIsConfirmingShip(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn-app btn-app--primary op-next"
                onClick={() => handleStatusChange()}
                disabled={busy || Boolean(holdForLots)}
                title={holdForLots}
                data-track="order_move_next"
              >
                {isNextStatusLoading ? (
                  <Spinner size="sm" />
                ) : (
                  `Move to ${data.nextStatus}`
                )}
              </button>
            )}
          </div>
        ) : null}
      </div>
      <DeleteConfirmationModal
        onDelete={handleDelete}
        onHide={() => setIsCancelOpen(false)}
        show={isCancelOpen}
        id={data?.id}
        label="cancel"
        buttonType="Cancel Order"
        isLoading={isDeleteLoading}
      />
    </>
  );
};

export default OrderDetailsModal;
